import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { setFilters, fetchTutors } from "../../../store/search";
import axios from "axios";
import { Autocomplete, TextField, MenuItem, Tooltip, IconButton } from "@mui/material";
import { useLocation } from "react-router-dom";
import { RestartAltOutlined } from '@mui/icons-material';

const SubjectSearch = ({ setSubject, setSelectedLevel, setLoading, backgroundColor, textColor }) => {
  const [subjects, setSubjects] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [isFetchingSubjects, setIsFetchingSubjects] = useState(false);
  // Add inside SubjectSearch component

  const [level, setLevel] = useState(""); // Make sure level has a default value

  const dispatch = useDispatch(); // Use the dispatch hook
  const location = useLocation();
  
  // Fetch subjects from API
  useEffect(() => {
    const fetchSearchSubjects = async () => {
      setIsFetchingSubjects(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER}/api/subjects?limit=1000`
        );
        setSubjects(response.data.subjects);
        setFilteredSubjects(response.data.subjects); // Default to first 10 subjects
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
      setIsFetchingSubjects(false);
    };

    fetchSearchSubjects();
  }, []);

  const { subject: reduxSubject, level: reduxLevel } = useSelector(
    (state) => state.search.filters
  );
  
  // Add effect to clear local state when Redux filters are cleared
  useEffect(() => {
    if (!reduxSubject || reduxSubject === "") {
      setSelectedSubject(null);
      setLevel("");
      setAvailableLevels([]);
    }
    if (!reduxLevel || reduxLevel === "") {
      setLevel("");
    }
  }, [reduxSubject, reduxLevel]);

  useEffect(() => {
    const runOnReduxSubject = async () => {
      if (reduxSubject && reduxSubject.length > 2) {
        setSelectedSubject(reduxSubject);
        setLevel(reduxLevel || "");
        // Sync parent state so Search button validation passes
        if (setSubject) setSubject(reduxSubject);
        if (setSelectedLevel) setSelectedLevel(reduxLevel || "");

        // Get levels
        const subjectData = subjects.find((sub) => sub.name === reduxSubject);
        setAvailableLevels(subjectData ? subjectData.levels : []);

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER}/api/subjects?search=${reduxSubject}`
          );
          setFilteredSubjects(response.data.subjects);
        } catch (error) {
          console.error("Error fetching filtered subjects from redux:", error);
        }
      }
    };

    runOnReduxSubject();
  }, [reduxSubject, subjects]);

  const { filters } = useSelector((state) => state.search);
  
  // Helper function to trigger search with current filters
  const triggerSearch = (newSubject, newLevel) => {
    const filterParams = {
      subject: newSubject || undefined,
      level: newLevel || undefined,
      price: filters.price.join(","),
    };
    
    if (filters.isOnline > -1) filterParams.isOnline = filters.isOnline;
    if (filters.isOnline === 0) {
      filterParams.distance = filters.distance;
      filterParams.latitude = filters.latitude;
      filterParams.longitude = filters.longitude;
    }
    if (filters.gender > -1) filterParams.gender = filters.gender;
    if (filters.exam) filterParams.exam = filters.exam;
    
    if (setLoading) setLoading(true);
    dispatch(fetchTutors({ filterParams, setLoading: setLoading || (() => {}) }));
  };

  // Handle subject selection
  const handleSubjectChange = (_, value) => {
    setSelectedSubject(value);
    setSubject(value);
    setSelectedLevel(""); // Reset the level when a new subject is selected
    const subjectData = subjects.find((sub) => sub.name === value);
    setAvailableLevels(subjectData ? subjectData.levels : []);
    setLevel(""); // Update the state with the selected value

    // Update the Redux filters with the selected subject
    dispatch(setFilters({ subject: value, level: "" })); // Clear level as subject changes
    
    // Trigger search with new subject
    triggerSearch(value, "");
  };

  // Handle the input change of the Autocomplete component
  const handleSubjectInputChange = async (_, value) => {
    if (value.length > 0) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER}/api/subjects?search=${value}`
        );
        setFilteredSubjects(response.data.subjects);
      } catch (error) {
        console.error("Error fetching filtered subjects:", error);
      }
    } else {
      setFilteredSubjects(subjects); // Reset to the first 10 subjects
    }
  };

  // Handle level selection and update filters
  const handleLevelChange = (e) => {
    const selectedLevelValue = e.target.value;
    setSelectedLevel(selectedLevelValue);
    setLevel(selectedLevelValue); // Update the state with the selected value

    // Update Redux store with selected level
    dispatch(setFilters({ level: selectedLevelValue }));
    
    // Trigger search with new level
    triggerSearch(selectedSubject, selectedLevelValue);
  };

  return (
    <div className="searchbar">
      <div className="inputs-container">
                  <Autocomplete
            options={filteredSubjects.map((subject) => subject.name)}
            value={selectedSubject || ""}
            onInputChange={handleSubjectInputChange} // Use the updated handler
            onChange={handleSubjectChange}
            sx={{
              ...(location.pathname === '/search' && {
                width: '40%', // Smaller width for search page to accommodate reset button
              }),
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Enter your subject"
                variant="filled"
                className="subject-input"
              sx={{
                '& .MuiFilledInput-root': {
                  backgroundColor: backgroundColor || 'transparent',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: backgroundColor || 'transparent',
                    border: '1px solid #999',
                  },
                  '&.Mui-focused': {
                    backgroundColor: backgroundColor || 'transparent',
                    border: '2px solid #712d93',
                  },
                },
                '& .MuiFilledInput-underline': {
                  '&:before': {
                    borderBottom: 'none !important',
                  },
                  '&:after': {
                    borderBottom: 'none !important',
                  },
                  '&:hover:before': {
                    borderBottom: 'none !important',
                  },
                  '&:hover:after': {
                    borderBottom: 'none !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  '&.Mui-focused': {
                    color: '#712d93',
                  },
                  '&.MuiFormLabel-filled': {
                    color: level ? '#712d93' : '#666',
                  },
                },
              }}
            />
          )}
        />

        {selectedSubject && (
          <div
            style={{
              display: "flex",
              flexDirection: location.pathname === '/search' ? "row" : "column",
              flexWrap: "nowrap",
              alignItems: location.pathname === '/search' ? "center" : "flex-end",
              gap: location.pathname === '/search' ? "8px" : "8px",
              width: location.pathname === '/search' ? "60%" : "auto",
            }}
          >
            <TextField
              select
              label="Level"
              value={level || ""} // Ensure level is set to a valid value or empty string
              variant="filled"
              className="level-input"
              onChange={handleLevelChange} // Use the level change handler
              sx={{
                ...(location.pathname === '/search' && {
                  flex: 1, // Take up remaining space in the container
                }),
                '& .MuiFilledInput-root': {
                  backgroundColor: backgroundColor || 'transparent',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: backgroundColor || 'transparent',
                    border: '1px solid #999',
                  },
                  '&.Mui-focused': {
                    backgroundColor: backgroundColor || 'transparent',
                    border: '2px solid #712d93',
                  },
                },
                '& .MuiFilledInput-underline': {
                  '&:before': {
                    borderBottom: 'none !important',
                  },
                  '&:after': {
                    borderBottom: 'none !important',
                  },
                  '&:hover:before': {
                    borderBottom: 'none !important',
                  },
                  '&:hover:after': {
                    borderBottom: 'none !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  '&.Mui-focused': {
                    color: '#712d93',
                  },
                  '&.MuiFormLabel-filled': {
                    color: selectedSubject ? '#712d93' : '#666',
                  },
                },
              }}
            >
              {availableLevels.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>

            {location.pathname !== '/search' && (
              <button
                type="button"
                onClick={() => {
                  handleSubjectChange({ target: { value: "" } });
                  handleLevelChange({ target: { value: "" } });
                }}
                style={{
                  backgroundColor: "transparent",
                  color: textColor || "#712d94",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                className="clear-btn"
              >
                <span>Reset Filter</span>
              </button>
            )}
            
            {location.pathname === '/search' && (
              <>
                <Tooltip title="Reset Filter" placement="top">
                  <IconButton
                      onClick={() => {
                        handleSubjectChange({ target: { value: "" } });
                        handleLevelChange({ target: { value: "" } });
                      }}
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        cursor: "pointer",
                        padding: "16px 12px",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                    <RestartAltOutlined color="primary" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </div>
        )}
        {location.pathname === '/search' && selectedSubject && (
          <button
            type="button"
            onClick={() => {
              setSelectedSubject(null);
              setLevel("");
              setAvailableLevels([]);
              if (setSubject) setSubject(null);
              if (setSelectedLevel) setSelectedLevel("");

              const defaultFilters = {
                subject: null,
                subjectLevel: null,
                level: null,
                distance: 7,
                gender: -1,
                isOnline: -1,
                price: [15, 100],
                load: 1,
                latitude: -1,
                longitude: -1,
                exam: null,
                postcode: "",
              };
              dispatch(setFilters(defaultFilters));

              if (setLoading) setLoading(true);
              dispatch(fetchTutors({ filterParams: { price: "15,100" }, setLoading: setLoading || (() => {}) }));
            }}
            className="reset-all-filters-btn"
          >
            Reset all filters
          </button>
        )}
      </div>
    </div>
  );
};

export default SubjectSearch;
